import {
  useGetAgencyMembers,
  useInvalidateUseGetAgencyMembers,
  useGetTeamsForAgency,
} from '@urbanx/agx.agencies.client';
import {
  AgxButton,
  AgxColumn,
  AgxEmptyState,
  AgxRow,
  Icon_PlusOutline,
} from '@urbanx/agx.ui-components2';
import LoadingState from 'components/loadingState/LoadingState';
import useAgencyIdParam from 'hooks/useAgencyIdParam';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import TeamDisplay from './TeamDisplay';

import TeamPageHeader from '../TeamPageHeader/TeamPageHeader';
import { TeamPageTabs } from '../TeamPageHeader/TeamPageTabs';

const ManageTeamsPage = () => {
  const agencyId = useAgencyIdParam();
  const invalidateAgencyMembers = useInvalidateUseGetAgencyMembers();

  const { data: teamsForAgency, isPending: isTeamsForAgencyPending } = useGetTeamsForAgency(
    agencyId ?? '',
    !!agencyId
  );

  const { data: agencyMembers, isPending: isAgencyMembersPending } = useGetAgencyMembers(
    agencyId ?? '',
    !!agencyId
  );

  useEffect(() => {
    if (!agencyId) return;
    void invalidateAgencyMembers(agencyId);
  }, [agencyId, invalidateAgencyMembers]);

  const orderedTeams = useMemo(() => {
    return (
      teamsForAgency?.sort((a, b) => {
        if (a?.name === undefined) return -1;
        if (b?.name === undefined) return 1;
        return a.name.localeCompare(b.name);
      }) ?? []
    );
  }, [teamsForAgency]);

  const isAnythingLoading = useMemo(() => {
    return isTeamsForAgencyPending || isAgencyMembersPending;
  }, [isTeamsForAgencyPending, isAgencyMembersPending]);

  return (
    <AgxColumn fill gap="spacing-xl">
      <TeamPageHeader
        selectedTab={TeamPageTabs.ManageTeams}
        actionButtons={
          <AgxButton
            id="addNewTeamActionButton"
            text="New Team"
            variant="primary"
            size="medium"
            rightIcon={<Icon_PlusOutline color="#ffffff" height={12} width={12} />}
            renderLink={(props) => <Link to="./add" {...props} />}
          />
        }
      />
      {!(!isAnythingLoading && (!teamsForAgency || teamsForAgency.length === 0)) ? (
        <AgxRow fill gap="spacing-xl" wrap>
          {orderedTeams.map((team) => (
            <TeamDisplay
              key={team.id}
              team={team}
              to={`./edit/${team.id ?? ''}`}
              agents={agencyMembers ?? []}
            />
          ))}
        </AgxRow>
      ) : (
        <AgxRow fill alignItems="alignCenter" justifyContent="center">
          <AgxEmptyState
            variant="advanced"
            label="No team created."
            glyphType={'women'}
            actionButtons={[
              {
                isPrimary: true,
                label: 'New team',
                order: 1,
                renderLink: (props) => <Link to={'./add'} {...props} />,
                rightIcon: <Icon_PlusOutline color="#ffffff" height={12} width={12} />,
              },
            ]}
          />
        </AgxRow>
      )}
      {isAnythingLoading && <LoadingState />}
    </AgxColumn>
  );
};

export default ManageTeamsPage;
